import type { TreeNode } from '../types/findingHierarchy';

/** The committed-statement text a leaf represents (matches the selection model). */
const valueOf = (node: TreeNode): string => node.displayValue ?? node.value;

/** Drop the all-normal summary from the committed list (picking any statement
 *  exits the all-normal state). No-op when the class has no all-normal text. */
export const withoutSummary = (
  current: string[],
  summary?: string
): string[] =>
  summary ? current.filter((s) => s.trim() !== summary.trim()) : current;

/**
 * Single-select toggle within one attribute row. Removes every statement
 * belonging to `rowLeaves`, then re-adds `leaf` unless it was the selected one.
 * So clicking a different pill swaps; clicking the selected pill clears.
 */
export const toggleStatementInRow = (
  current: string[],
  leaf: TreeNode,
  rowLeaves: TreeNode[]
): string[] => {
  const target = valueOf(leaf).trim();
  const wasSelected = current.some((s) => s.trim() === target);
  const rowValues = new Set(rowLeaves.map((l) => valueOf(l).trim()));
  const withoutRow = current.filter((s) => !rowValues.has(s.trim()));
  return wasSelected ? withoutRow : [...withoutRow, valueOf(leaf)];
};

const classLeafValues = (classRows: TreeNode[]): Set<string> =>
  new Set(
    classRows.flatMap((row) => row.children.map((l) => valueOf(l).trim()))
  );

/**
 * Toggle a finding class to/from its collapsed "all normal" summary. Turning on
 * drops every individual statement belonging to the class and adds the single
 * summary (the class's dedicated "Normal" statement text); turning off just
 * drops it. If the class has no all-normal text, turning on simply clears the
 * class and inserts nothing.
 */
export const applyAllNormal = (
  current: string[],
  classRows: TreeNode[],
  summary: string | undefined,
  turnOn: boolean
): string[] => {
  const inClass = classLeafValues(classRows);
  const cleaned = current.filter(
    (s) => !inClass.has(s.trim()) && s.trim() !== summary?.trim()
  );
  return turnOn && summary ? [...cleaned, summary] : cleaned;
};

/** True when the class is currently collapsed to its all-normal summary. */
export const isAllNormalActive = (
  current: string[],
  summary?: string
): boolean => !!summary && current.some((s) => s.trim() === summary.trim());

/**
 * Reorder a committed statement list to match the attribute row order.
 * Row-recognised statements are placed by row index (ascending); anything
 * else (free-text additions, all-normal summary text) is preserved at the
 * tail in its original relative order.
 *
 * `rows` must span every class the caller may draw a pick from — for a
 * group that pools multiple classes (e.g. "Atria" = LEFT_ATRIUM +
 * RIGHT_ATRIUM), pass every class's rows concatenated in display order,
 * otherwise selections from non-active classes get demoted to the tail.
 *
 * Precomputes a leaf-text → row-index map so the per-statement lookup is
 * O(1); overall O(rows·leaves + statements).
 */
export const orderByRow = (
  statements: string[],
  rows: TreeNode[]
): string[] => {
  const rowIdxByLeafText = new Map<string, number>();
  rows.forEach((row, rowIdx) => {
    row.children.forEach((l) => {
      rowIdxByLeafText.set(valueOf(l).trim(), rowIdx);
    });
  });
  const indexed = statements.map((value, origIdx) => ({
    value,
    rowIdx: rowIdxByLeafText.get(value.trim()) ?? -1,
    origIdx,
  }));
  return indexed
    .sort((a, b) => {
      if (a.rowIdx === -1 && b.rowIdx === -1) return a.origIdx - b.origIdx;
      if (a.rowIdx === -1) return 1;
      if (b.rowIdx === -1) return -1;
      if (a.rowIdx === b.rowIdx) return a.origIdx - b.origIdx;
      return a.rowIdx - b.rowIdx;
    })
    .map((entry) => entry.value);
};
